import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, CheckCircle, XCircle, Plus, Edit, Trash2, Search, FileSpreadsheet, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ImportarCodigosAnomaliaDialog } from "@/components/anomalias/ImportarCodigosAnomaliaDialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CodigoAnomalia } from "@/hooks/useAnomalias";
import { 
  ALL_SYSTEM_TYPES, 
  getSystemTypeLabel, 
  getSystemCategory,
  SECURITY_SYSTEM_TYPES,
  PCI_SYSTEM_TYPES
} from "@/lib/systemTypeConstants";

const SEVERITIES = [
  { value: "baja", label: "Baja", color: "bg-green-500", icon: CheckCircle },
  { value: "media", label: "Media", color: "bg-yellow-500", icon: AlertTriangle },
  { value: "alta", label: "Alta", color: "bg-red-500", icon: XCircle },
];

export default function GestionCodigosAnomalias() {
  const [codigos, setCodigos] = useState<CodigoAnomalia[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState<string>("all");
  const [filterCategoria, setFilterCategoria] = useState<string>("all");
  const [filterSeveridad, setFilterSeveridad] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingCodigo, setEditingCodigo] = useState<CodigoAnomalia | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    codigo: "",
    descripcion: "",
    tipo_sistema: "",
    severidad: "media",
    solucion_sugerida: "",
    referencia_normativa: "",
    justificacion_normativa: "",
  });

  const loadCodigos = async () => {
    try {
      const { data, error } = await supabase
        .from('codigos_anomalias')
        .select('*')
        .order('codigo');

      if (error) throw error;
      setCodigos(data || []);
    } catch (error) {
      console.error('Error loading anomaly codes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los códigos de anomalías",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCodigo) {
        const { error } = await supabase
          .from('codigos_anomalias')
          .update(formData)
          .eq('id', editingCodigo.id);

        if (error) throw error;
        
        toast({
          title: "Código actualizado",
          description: "El código de anomalía ha sido actualizado correctamente",
        });
      } else {
        const { error } = await supabase
          .from('codigos_anomalias')
          .insert([formData]);

        if (error) throw error;
        
        toast({
          title: "Código creado",
          description: "El código de anomalía ha sido creado correctamente",
        });
      }

      resetForm();
      setDialogOpen(false);
      await loadCodigos();
    } catch (error) {
      console.error('Error saving anomaly code:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar el código de anomalía",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (codigo: CodigoAnomalia) => {
    setEditingCodigo(codigo);
    setFormData({
      codigo: codigo.codigo,
      descripcion: codigo.descripcion,
      tipo_sistema: codigo.tipo_sistema,
      severidad: codigo.severidad,
      solucion_sugerida: codigo.solucion_sugerida || "",
      referencia_normativa: codigo.referencia_normativa || "",
      justificacion_normativa: codigo.justificacion_normativa || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Está seguro de que desea eliminar este código de anomalía?")) {
      return;
    }

    try {
      const { error } = await supabase
        .from('codigos_anomalias')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Código eliminado",
        description: "El código de anomalía ha sido eliminado correctamente",
      });

      await loadCodigos();
    } catch (error) {
      console.error('Error deleting anomaly code:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el código de anomalía",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      codigo: "",
      descripcion: "",
      tipo_sistema: "",
      severidad: "media",
      solucion_sugerida: "",
      referencia_normativa: "",
      justificacion_normativa: "",
    });
    setEditingCodigo(null);
  };

  const getSeverityBadge = (severidad: string) => {
    const severity = SEVERITIES.find(s => s.value === severidad);
    if (!severity) return null;

    const Icon = severity.icon;
    return (
      <Badge variant="secondary" className={`${severity.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {severity.label}
      </Badge>
    );
  };

  const filteredCodigos = codigos.filter(codigo => {
    const matchesSearch = codigo.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         codigo.descripcion.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = filterTipo === "all" || codigo.tipo_sistema === filterTipo;
    const matchesCategoria = filterCategoria === "all" || getSystemCategory(codigo.tipo_sistema) === filterCategoria;
    const matchesSeveridad = filterSeveridad === "all" || codigo.severidad === filterSeveridad;
    
    return matchesSearch && matchesTipo && matchesCategoria && matchesSeveridad;
  });

  useEffect(() => {
    loadCodigos();
  }, []);

  return (
    <div className="w-full p-4 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Gestión de Códigos de Anomalías</h1>
          <p className="text-muted-foreground">
            Administra los códigos de anomalías del sistema
          </p>
        </div>
        
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover z-50">
              <DropdownMenuItem onClick={() => setImportDialogOpen(true)}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Importar desde Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Código
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingCodigo ? "Editar" : "Crear"} Código de Anomalía
              </DialogTitle>
              <DialogDescription>
                {editingCodigo ? "Modifica" : "Crea"} un código de anomalía para el sistema
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="codigo">Código *</Label>
                  <Input
                    id="codigo"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    placeholder="ej: AL001"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tipo_sistema">Tipo de Sistema *</Label>
                  <Select
                    value={formData.tipo_sistema}
                    onValueChange={(value) => setFormData({ ...formData, tipo_sistema: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar sistema" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                        Sistemas de Seguridad
                      </div>
                      {Object.entries(SECURITY_SYSTEM_TYPES).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                        Sistemas PCI
                      </div>
                      {Object.entries(PCI_SYSTEM_TYPES).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción *</Label>
                <Textarea
                  id="descripcion"
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Describe la anomalía..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="severidad">Severidad</Label>
                <Select
                  value={formData.severidad}
                  onValueChange={(value) => setFormData({ ...formData, severidad: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((severity) => (
                      <SelectItem key={severity.value} value={severity.value}>
                        {severity.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="solucion_sugerida">Solución Sugerida</Label>
                <Textarea
                  id="solucion_sugerida"
                  value={formData.solucion_sugerida}
                  onChange={(e) => setFormData({ ...formData, solucion_sugerida: e.target.value })}
                  placeholder="Describe la solución sugerida..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="referencia_normativa">Referencia normativa</Label>
                <Input
                  id="referencia_normativa"
                  value={formData.referencia_normativa}
                  onChange={(e) => setFormData({ ...formData, referencia_normativa: e.target.value })}
                  placeholder="ej: RIPCI - RD 513/2017, Art. 21.3"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="justificacion_normativa">Justificación normativa (extracto)</Label>
                <Textarea
                  id="justificacion_normativa"
                  rows={4}
                  value={formData.justificacion_normativa}
                  onChange={(e) => setFormData({ ...formData, justificacion_normativa: e.target.value })}
                  placeholder="Pega aquí el extracto literal de la normativa que justifica esta anomalía…"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {editingCodigo ? "Actualizar" : "Crear"} Código
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ImportarCodigosAnomaliaDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={() => loadCodigos()}
      />

      {/* Filters */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="search">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Buscar por código o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="filter-categoria">Categoría</Label>
              <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="seguridad">Seguridad</SelectItem>
                  <SelectItem value="pci">PCI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="filter-tipo">Tipo de Sistema</Label>
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(ALL_SYSTEM_TYPES).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="filter-severidad">Severidad</Label>
              <Select value={filterSeveridad} onValueChange={setFilterSeveridad}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {SEVERITIES.map((severity) => (
                    <SelectItem key={severity.value} value={severity.value}>
                      {severity.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">
            Códigos de Anomalías ({filteredCodigos.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20 whitespace-nowrap">Código</TableHead>
                  <TableHead className="w-32 whitespace-nowrap">Sistema</TableHead>
                  <TableHead className="w-48">Descripción</TableHead>
                  <TableHead className="w-24 whitespace-nowrap">Severidad</TableHead>
                  <TableHead className="w-40">Solución</TableHead>
                  <TableHead className="w-36 whitespace-nowrap">Ref. normativa</TableHead>
                  <TableHead className="w-56">Justificación</TableHead>
                  <TableHead className="w-20 whitespace-nowrap">Acciones</TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
              {filteredCodigos.map((codigo) => (
                <TableRow key={codigo.id}>
                  <TableCell className="font-mono font-medium whitespace-nowrap">
                    {codigo.codigo}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Badge variant="outline">
                      {getSystemTypeLabel(codigo.tipo_sistema)}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="truncate" title={codigo.descripcion}>
                      {codigo.descripcion}
                    </div>
                  </TableCell>
                  <TableCell>
                    {getSeverityBadge(codigo.severidad)}
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="truncate" title={codigo.solucion_sugerida}>
                      {codigo.solucion_sugerida || "N/A"}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[180px]">
                    <div className="truncate text-xs" title={codigo.referencia_normativa || ""}>
                      {codigo.referencia_normativa || <span className="text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="truncate text-xs text-muted-foreground" title={codigo.justificacion_normativa || ""}>
                      {codigo.justificacion_normativa || "—"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(codigo)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(codigo.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>

          
          {filteredCodigos.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || filterTipo !== "all" || filterCategoria !== "all" || filterSeveridad !== "all" 
                ? "No se encontraron códigos con los filtros aplicados"
                : "No hay códigos de anomalías registrados"
              }
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}